// ============================================================================
// MetasAdmin.js — aba "Metas e Equipes" self-service.
// Loja: vê/edita só as suas. Superadmin: todas (com coluna Loja).
// Metas escrevem nas tabelas que o Sales Day lê; Equipe escreve o headcount.
// ============================================================================
import React, { useState, useEffect, useCallback } from 'react';
import { apiListMetas, apiSetMeta, apiListEquipes, apiSetEquipe, getSiteMeta, saveSiteMeta } from '../api';

const SAMSUNG = "'Samsung Sharp Sans', 'SamsungOne', 'SamsungOne UI', sans-serif";

const fmtBRL = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);
const fmtNum = (v) => new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(Number(v) || 0);
// "50.000" / "50000" / "50.000,00" → 50000  (moeda)
const parseBRL = (s) => {
  const n = Number(String(s).replace(/[^\d.,-]/g, '').replace(/\./g, '').replace(',', '.'));
  return isFinite(n) ? n : 0;
};
// "3,5" / "3.5" → 3.5  (quantidade)
const parseNum = (s) => {
  const n = Number(String(s).replace(',', '.').replace(/[^\d.-]/g, ''));
  return isFinite(n) ? n : 0;
};

// Tabela editável genérica (usada por Metas e por Equipe).
function EditableTable({ title, subtitle, rows, isSuper, kind, idField, onSave }) {
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [savedKey, setSavedKey] = useState(null);
  const [erro, setErro] = useState('');

  const keyOf = (r) => `${r.loja_slug}|${r[idField]}`;
  const fmt = kind === 'brl' ? fmtBRL : fmtNum;
  const parse = kind === 'brl' ? parseBRL : parseNum;
  const display = (v) => (kind === 'brl'
    ? new Intl.NumberFormat('pt-BR').format(Number(v) || 0)
    : fmtNum(v));

  // Valor ATUAL de um field de uma loja (rascunho se editado, senão o salvo).
  const curVal = (loja, field) => {
    const k = `${loja}|${field}`;
    if (k in drafts) return parseNum(drafts[k]);
    const sib = rows.find((r) => r.loja_slug === loja && r[idField] === field);
    return sib ? Number(sib.valor) || 0 : 0;
  };
  const dec1 = (x) => new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(x || 0);
  // Detalhamento do rateio dos técnicos híbridos (0,5 p/ LM + 0,5 p/ LB) na aba Equipe.
  const equipeHint = (r) => {
    if (kind !== 'num') return null;
    const f = r[idField], loja = r.loja_slug;
    const hib = curVal(loja, 'tecnicos_lm_lb');
    const share = hib * 0.5;
    if (f === 'tecnicos_lm') {
      const base = curVal(loja, 'tecnicos_lm');
      return share > 0 ? `Considerado: ${dec1(base + share)} — ${dec1(base)} LM + ${dec1(share)} dos híbridos` : null;
    }
    if (f === 'tecnicos_lb') {
      const base = curVal(loja, 'tecnicos_lb');
      return share > 0 ? `Considerado: ${dec1(base + share)} — ${dec1(base)} LB + ${dec1(share)} dos híbridos` : null;
    }
    if (f === 'tecnicos_lm_lb') {
      return hib > 0 ? `Distribuição: ${dec1(share)} p/ LM + ${dec1(share)} p/ Linha Branca` : null;
    }
    return null;
  };

  const save = async (r, raw) => {
    const valor = parse(raw);
    const k = keyOf(r);
    if (valor === Number(r.valor)) { setDrafts((d) => { const c = { ...d }; delete c[k]; return c; }); return; }
    setSavingKey(k); setErro('');
    try {
      await onSave(r, valor);
      r.valor = valor; // muta a linha local (rows é a fonte)
      setDrafts((d) => { const c = { ...d }; delete c[k]; return c; });
      setSavedKey(k); setTimeout(() => setSavedKey((cur) => (cur === k ? null : cur)), 1600);
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSavingKey(null);
    }
  };

  const th = { textAlign: 'left', padding: '12px 18px', fontSize: 12, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '2px solid #e2e8f0' };
  const td = { padding: '12px 18px', borderBottom: '1px solid #eef2f6', fontFamily: SAMSUNG };
  const cellInput = {
    width: kind === 'brl' ? 150 : 90, padding: '8px 11px', borderRadius: 9, border: '1px solid #d9dde3',
    background: '#fff', fontSize: 15, fontWeight: 700, fontFamily: SAMSUNG, color: '#1428A0',
    outline: 'none', textAlign: 'right',
  };

  return (
    <div style={{ marginBottom: 30 }}>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1428A0', margin: '0 0 4px' }}>{title}</h3>
      {subtitle && <p style={{ fontSize: 13.5, color: '#64748b', margin: '0 0 12px' }}>{subtitle}</p>}
      {erro && <div style={{ marginBottom: 12, padding: '10px 13px', borderRadius: 10, background: 'rgba(255,67,55,0.08)', border: '1px solid rgba(255,67,55,0.22)', color: '#D63A2F', fontSize: 13.5, fontWeight: 600 }}>{erro}</div>}
      <div style={{ background: '#fff', border: '1px solid #e8edf3', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 10px rgba(20,40,160,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f7f8fa' }}>
              {isSuper && <th style={th}>Loja</th>}
              <th style={th}>{kind === 'brl' ? 'Métrica' : 'Função'}</th>
              <th style={{ ...th, textAlign: 'right' }}>{kind === 'brl' ? 'Meta (R$)' : 'Quantidade'}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const k = keyOf(r);
              const prev = rows[i - 1];
              const newLoja = isSuper && (!prev || prev.loja_slug !== r.loja_slug);
              return (
                <tr key={k} style={{ borderTop: newLoja ? '2px solid #e2e8f0' : 'none' }}>
                  {isSuper && (
                    <td style={{ ...td, fontWeight: 700, color: newLoja ? '#1428A0' : 'transparent' }}>
                      {newLoja ? r.loja : ''}
                    </td>
                  )}
                  <td style={{ ...td, fontSize: 15.5, fontWeight: 600, color: '#1F2A44' }}>{r.label}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: savedKey === k ? '#008577' : savingKey === k ? '#94a3b8' : 'transparent', minWidth: 42, textAlign: 'right' }}>
                        {savedKey === k ? '✓ salvo' : savingKey === k ? '...' : ''}
                      </span>
                      {kind === 'brl' && r.unit !== 'num' && <span style={{ fontSize: 14, fontWeight: 700, color: '#94a3b8' }}>R$</span>}
                      {r.unit === 'num' && <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8' }}>GDs</span>}
                      <input
                        style={cellInput}
                        value={drafts[k] ?? display(r.valor)}
                        onChange={(e) => setDrafts((d) => ({ ...d, [k]: e.target.value }))}
                        onBlur={(e) => save(r, e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        inputMode="decimal"
                        title={kind === 'brl' ? `Atual: ${fmt(r.valor)}` : `Atual: ${fmt(r.valor)}`}
                      />
                    </div>
                    {equipeHint(r) && (
                      <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, marginTop: 3, textAlign: 'right' }}>{equipeHint(r)}</div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Seção "Meta do Site" — GERAL (não por loja): Meta B2C e Meta B2B do e-commerce.
function SiteMetaSection({ siteMeta, isSuper, onSaved }) {
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [savedKey, setSavedKey] = useState(null);
  const [erro, setErro] = useState('');
  const rows = [{ canal: 'b2c', label: 'Meta E-commerce B2C' }, { canal: 'b2b', label: 'Meta E-commerce B2B' }];
  const disp = (v) => new Intl.NumberFormat('pt-BR').format(Number(v) || 0);

  const save = async (canal, raw) => {
    const valor = parseBRL(raw);
    if (valor === Number(siteMeta[canal])) { setDrafts((d) => { const c = { ...d }; delete c[canal]; return c; }); return; }
    setSavingKey(canal); setErro('');
    try {
      await saveSiteMeta({ canal, valor });
      setDrafts((d) => { const c = { ...d }; delete c[canal]; return c; });
      setSavedKey(canal); setTimeout(() => setSavedKey((k) => (k === canal ? null : k)), 1600);
      onSaved();
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao salvar');
    } finally { setSavingKey(null); }
  };

  const th = { textAlign: 'left', padding: '12px 18px', fontSize: 12, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '2px solid #e2e8f0' };
  const td = { padding: '12px 18px', borderBottom: '1px solid #eef2f6', fontFamily: SAMSUNG };
  const cellInput = { width: 150, padding: '8px 11px', borderRadius: 9, border: '1px solid #d9dde3', background: '#fff', fontSize: 15, fontWeight: 700, fontFamily: SAMSUNG, color: '#1428A0', outline: 'none', textAlign: 'right' };

  return (
    <div style={{ marginBottom: 30 }}>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1428A0', margin: '0 0 4px' }}>Meta do Site (E-commerce)</h3>
      <p style={{ fontSize: 13.5, color: '#64748b', margin: '0 0 12px' }}>Meta mensal GERAL do site (não é por loja). Alimenta o "esperado" dos slides Vendas E-commerce B2C e B2B.</p>
      {erro && <div style={{ marginBottom: 12, padding: '10px 13px', borderRadius: 10, background: 'rgba(255,67,55,0.08)', border: '1px solid rgba(255,67,55,0.22)', color: '#D63A2F', fontSize: 13.5, fontWeight: 600 }}>{erro}</div>}
      <div style={{ background: '#fff', border: '1px solid #e8edf3', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 10px rgba(20,40,160,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#f7f8fa' }}><th style={th}>Canal</th><th style={{ ...th, textAlign: 'right' }}>Meta (R$)</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.canal}>
                <td style={{ ...td, fontSize: 15.5, fontWeight: 600, color: '#1F2A44' }}>{r.label}</td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: savedKey === r.canal ? '#008577' : savingKey === r.canal ? '#94a3b8' : 'transparent', minWidth: 42, textAlign: 'right' }}>
                      {savedKey === r.canal ? '✓ salvo' : savingKey === r.canal ? '...' : ''}
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#94a3b8' }}>R$</span>
                    <input style={{ ...cellInput, opacity: isSuper ? 1 : 0.6 }} disabled={!isSuper}
                      value={drafts[r.canal] ?? disp(siteMeta[r.canal])}
                      onChange={(e) => setDrafts((d) => ({ ...d, [r.canal]: e.target.value }))}
                      onBlur={(e) => isSuper && save(r.canal, e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                      inputMode="decimal" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function MetasAdmin() {
  const [metas, setMetas] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [siteMeta, setSiteMeta] = useState({ b2c: 0, b2b: 0 });
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([apiListMetas(), apiListEquipes(), getSiteMeta().catch(() => null)])
      .then(([mr, er, sr]) => {
        setMetas(mr.data.metas || []);
        setEquipes(er.data.equipes || []);
        setSiteMeta(sr?.data?.metas || { b2c: 0, b2b: 0 });
        setRole(mr.data.role);
      })
      .catch((e) => setErro(e.response?.data?.detail || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const isSuper = role === 'superadmin';

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '8px 4px 40px', fontFamily: SAMSUNG }}>
      <h2 style={{ fontSize: 26, fontWeight: 700, color: '#1428A0', letterSpacing: '-0.02em', margin: '4px 0 4px' }}>Metas e Equipe</h2>
      <p style={{ fontSize: 14, fontWeight: 400, color: '#64748b', margin: '0 0 24px' }}>
        {isSuper ? 'Você vê e edita as metas e a equipe de todas as lojas.' : 'Edite as metas e a equipe da sua loja.'} As mudanças entram direto no Sales Day 2.0.
      </p>

      {erro && <div style={{ marginBottom: 14, padding: '10px 13px', borderRadius: 10, background: 'rgba(255,67,55,0.08)', border: '1px solid rgba(255,67,55,0.22)', color: '#D63A2F', fontSize: 13.5, fontWeight: 600 }}>{erro}</div>}

      {loading ? (
        <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>Carregando…</div>
      ) : (
        <>
          <EditableTable
            title="Metas (R$)"
            subtitle="Meta mensal de cada métrica do Sales Day. Store+/SMB são do mês atual."
            rows={metas} isSuper={isSuper} kind="brl" idField="metric_key"
            onSave={(r, valor) => apiSetMeta({ loja_slug: r.loja_slug, metric_key: r.metric_key, valor })}
          />
          <SiteMetaSection siteMeta={siteMeta} isSuper={isSuper} onSaved={load} />
          <EditableTable
            title="Equipe"
            subtitle="Quantidade de recepcionistas e técnicos (aceita meio, ex: 3,5). Alimenta produtividade no Sales Day."
            rows={equipes} isSuper={isSuper} kind="num" idField="field"
            onSave={(r, valor) => apiSetEquipe({ loja_slug: r.loja_slug, field: r.field, valor })}
          />
        </>
      )}
    </div>
  );
}
